"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import {
  DRIVER_TIP_PRESETS_CENTS,
  formatDriverTipCents,
  isValidDriverTipCents,
  parseDriverTipInput,
} from "@/lib/delivery/driverTip"

/**
 * Propina para el repartidor (spec 2026-09-12). Calcado de DonationStep:
 * presets en dólares fijos, monto libre acotado, salida explícita "Sin propina".
 * SIN preselección: el cliente elige o dice que no. El monto sale en CENTAVOS
 * enteros y ya validado; el servidor vuelve a validar el rango.
 */
interface Props {
  accentColor: string
  /** Al volver atrás desde pago se conserva lo elegido (0 = nada). */
  initialTipCents?: number
  onContinue: (tipCents: number) => void
  onBack: () => void
}

export function DriverTipStep({ accentColor, initialTipCents = 0, onContinue, onBack }: Props) {
  const t = useTranslations("public.microsite.driverTip")
  const presets = DRIVER_TIP_PRESETS_CENTS as readonly number[]
  const initialIsPreset = presets.includes(initialTipCents)
  const [selected, setSelected] = useState<number | null>(initialIsPreset ? initialTipCents : null)
  const [custom, setCustom] = useState(
    initialTipCents > 0 && !initialIsPreset ? (initialTipCents / 100).toFixed(2) : "",
  )

  const customCents = custom.trim() ? parseDriverTipInput(custom) : null
  const customInvalid = custom.trim().length > 0 && customCents === null
  const chosen = selected ?? customCents
  const canContinue = isValidDriverTipCents(chosen)

  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="text-center">
        <div className="text-4xl">🛵</div>
        <h2 className="text-xl font-black mt-1" style={{ color: "#1A1A1A" }}>{t("title")}</h2>
        <p className="text-sm text-gray-500 mt-1">{t("subtitle")}</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {presets.map((cents) => {
          const active = selected === cents
          return (
            <button
              key={cents}
              type="button"
              aria-pressed={active}
              onClick={() => { setSelected(cents); setCustom("") }}
              className="rounded-xl border px-3 py-3 text-center transition-all"
              style={{ borderColor: active ? accentColor : "#e5e7eb", borderWidth: active ? 2 : 1 }}
            >
              <span className="block text-lg font-black">{formatDriverTipCents(cents)}</span>
            </button>
          )
        })}
      </div>

      <label className="block">
        <span className="block text-xs font-semibold text-gray-500 mb-1">{t("custom")}</span>
        <input
          inputMode="decimal"
          placeholder={t("customPlaceholder")}
          value={custom}
          onChange={(e) => { setCustom(e.target.value); setSelected(null) }}
          aria-invalid={customInvalid || undefined}
          className="w-full rounded-xl border px-3 py-2 text-base"
          style={{ borderColor: customInvalid ? "#D7263D" : "#e5e7eb" }}
        />
        {customInvalid && <span className="block text-[11px] mt-1" style={{ color: "#D7263D" }}>{t("invalid")}</span>}
      </label>

      <button
        type="button"
        disabled={!canContinue}
        onClick={() => { if (isValidDriverTipCents(chosen)) onContinue(chosen) }}
        className="w-full rounded-xl py-3 font-bold text-white disabled:opacity-50"
        style={{ background: accentColor }}
      >
        {canContinue ? t("continueWithAmount", { amount: formatDriverTipCents(chosen as number) }) : t("title")}
      </button>
      <button type="button" onClick={() => onContinue(0)} className="text-sm text-gray-500">{t("noTip")}</button>
      <button type="button" onClick={onBack} className="text-sm text-gray-400">{t("back")}</button>
    </div>
  )
}
